import g from '#core/global';
import load from '#core/load';


const tab_$ = '[data-palette-tab]';
const content_$ = '[data-palette-content]';
const compare_toggle_$ = '[data-compare-toggle]';
const compare_overlay_$ = '[data-compare-overlay]';
const compare_backdrop_$ = '[data-compare-backdrop]';
const compare_close_$ = '[data-compare-close]';
const compare_grid_$ = '[data-compare-grid]';
const compare_title_$ = '[data-compare-title]';
const toolbar_$ = '[data-palette-toolbar]';

const active_class = 'is-active';
const compare_active_class = 'demo--palette--compare-active';
const highlight_class = 'demo--palette__compare-highlight';
const clickable_class = 'demo--palette__compare-clickable';
const storage_key = 'palette-demo-active-tab';

let $content_areas;
let $tabs;
let $compare_toggle;
let $overlay;
let $compare_grid;
let $compare_title;
let $page;
let compare_mode = false;
let has_demo = false;
let palette_count = 0;
let palette_keys = [];
let palette_ids = [];


const module = {
	/**
	 * Init module
	 */
	init() {
		if (load.ran('palette-demo')) return false;

		load.content(() => {
			has_demo = assign_elements();
			if (!has_demo) return false;

			init_tabs();
			init_compare_mode();
		});
	},
};

export default module;
module.init();


/**
 * Assign elements and read palette tab data
 * @return {boolean}
 */
function assign_elements() {
	if (has_demo) return true;

	$content_areas = document.querySelectorAll(content_$);
	$tabs = document.querySelectorAll(tab_$);
	$compare_toggle = document.querySelector(compare_toggle_$);
	$overlay = document.querySelector(compare_overlay_$);
	$compare_grid = document.querySelector(compare_grid_$);
	$compare_title = document.querySelector(compare_title_$);
	$page = document.querySelector('.palette-demo');

	// Read palette count, keys, and ids from the tab buttons. Ids come from
	// data-palette-tab rather than tab position: palettes disabled in Style
	// Studio render no tab (wishlist 213), so the rendered list can be sparse
	// while ids keep matching the palette indices in the compiled CSS.
	palette_count = $tabs.length;
	palette_keys = Array.from($tabs).map(($tab) => {
		const text = $tab.textContent.trim();
		const colon = text.indexOf(':');
		return colon >= 0 ? text.substring(colon + 1).trim() : text;
	});
	palette_ids = Array.from($tabs).map(($tab) => $tab.getAttribute('data-palette-tab'));

	return $content_areas.length > 0 && $tabs.length > 0;
}


// --- Tab switching (palette class swap) ---

/**
 * Initialize palette tabs and restore saved state
 */
function init_tabs() {
	// Restore saved tab — unless that palette's tab is gone (disabled in
	// Style Studio since the save)
	const saved = sessionStorage.getItem(storage_key);
	if (saved && palette_ids.includes(saved)) {
		activate_tab(saved);
	}

	// Delegated click handler for tabs
	document.addEventListener(g.click_e, (e) => {
		const $tab = e.target.closest(tab_$);
		if (!$tab) return;

		e.preventDefault();
		if (compare_mode) toggle_compare_mode();
		const palette_id = $tab.getAttribute('data-palette-tab');
		activate_tab(palette_id);
		sessionStorage.setItem(storage_key, palette_id);
	});
}


/**
 * Activate a palette tab by ID
 * @param {string} palette_id
 */
function activate_tab(palette_id) {
	// Update tab active states
	$tabs.forEach(($tab) => {
		const is_target = $tab.getAttribute('data-palette-tab') === palette_id;
		$tab.classList.toggle(active_class, is_target);
	});

	// Update page-level palette (drives :root palette via CSS :has())
	if ($page) {
		$page.setAttribute('data-palette-static', palette_id);
	}
}


// --- Compare mode ---

/**
 * Initialize compare mode event handlers
 */
function init_compare_mode() {
	if (!$compare_toggle || !$overlay) return;

	// Toggle compare mode
	document.addEventListener(g.click_e, (e) => {
		if (e.target.closest(compare_toggle_$)) {
			e.preventDefault();
			toggle_compare_mode();
			return;
		}

		// Close overlay
		if (e.target.closest(compare_backdrop_$) || e.target.closest(compare_close_$)) {
			e.preventDefault();
			close_compare_overlay();
			return;
		}

		// Click element in compare mode
		if (compare_mode && e.target.closest(content_$)) {
			const $target = find_compare_target(e.target);
			if ($target) {
				e.preventDefault();
				e.stopPropagation();
				open_compare_overlay($target);
			}
		}
	});

	// Disabled elements swallow click events; use pointerdown to catch them
	document.addEventListener('pointerdown', (e) => {
		if (!compare_mode) return;
		const $target = find_compare_target(e.target);
		if (!$target || !$target.disabled) return;
		e.preventDefault();
		e.stopPropagation();
		open_compare_overlay($target);
	}, true);

	// Hover highlight in compare mode
	document.addEventListener(g.mouse_over_e, (e) => {
		if (!compare_mode) return;
		const $content = e.target.closest(content_$);
		if (!$content) return;

		clear_highlights();
		const $target = find_compare_target(e.target);
		if ($target) {
			$target.classList.add(highlight_class);
		}
	});

	document.addEventListener(g.mouse_out_e, (e) => {
		if (!compare_mode) return;
		clear_highlights();
	});

	// Escape to close
	document.addEventListener(g.keydown_e, (e) => {
		if (e.key === 'Escape') {
			if ($overlay && !$overlay.hasAttribute(g.hidden_attr)) {
				close_compare_overlay();
			} else if (compare_mode) {
				toggle_compare_mode();
			}
		}
	});
}


/**
 * Toggle compare mode on or off
 */
function toggle_compare_mode() {
	compare_mode = !compare_mode;
	$compare_toggle.classList.toggle(active_class, compare_mode);

	if ($page) {
		$page.classList.toggle(compare_active_class, compare_mode);
	}

	toggle_disabled_elements(compare_mode);

	if (!compare_mode) {
		clear_highlights();
		close_compare_overlay();
	}
}


/**
 * Toggle clickable class on disabled elements when entering compare mode
 * @param {boolean} entering
 */
function toggle_disabled_elements(entering) {
	const selector = `${content_$} [data-pattern][disabled]`;
	document.querySelectorAll(selector).forEach(($el) => {
		$el.classList.toggle(clickable_class, entering);
	});
}


/**
 * Find the nearest compare target element
 * @param {HTMLElement} el
 * @return {HTMLElement|null}
 */
function find_compare_target(el) {
	const $content = el.closest(content_$);
	if (!$content) return null;
	return el.closest('[data-pattern]');
}


/**
 * Clear all compare-mode highlights
 */
function clear_highlights() {
	const $highlighted = document.querySelectorAll('.' + highlight_class);
	$highlighted.forEach(($el) => $el.classList.remove(highlight_class));
}


/**
 * Open the compare overlay with the target cloned across all palettes
 * @param {HTMLElement} $target
 */
function open_compare_overlay($target) {
	if (!$overlay || !$compare_grid) return;

	// Clear previous content
	$compare_grid.innerHTML = '';

	// Clone the clicked element once per rendered tab, applying that tab's
	// palette class
	for (let i = 0; i < palette_count; i++) {
		const palette_id = palette_ids[i];
		const key = palette_keys[i] || '';

		const cell = document.createElement('div');
		cell.className = `demo--palette__compare-cell :palette-${palette_id}`;

		// Floating palette label
		const label = document.createElement('span');
		label.className = 'demo--palette__compare-label';
		label.textContent = `${palette_id}: ${key}`;
		cell.appendChild(label);

		// Clone the target element
		const $clone = $target.cloneNode(true);
		$clone.classList.remove(highlight_class, clickable_class);
		strip_reveal_attrs($clone);

		cell.appendChild($clone);
		$compare_grid.appendChild(cell);
	}

	// Update title
	if ($compare_title) {
		const pattern = $target.getAttribute('data-pattern') || '';
		const utils = Array.from($target.classList).filter((c) => c.startsWith(':'));
		const sig_attrs = ['disabled', 'readonly', 'required'];
		const attrs = sig_attrs.filter((a) => $target.hasAttribute(a));

		const parts = [
			...utils,
			...attrs.map((a) => `[${a}]`),
		];
		const suffix = parts.length ? ` / ${parts.join(' ')}` : '';
		$compare_title.textContent = `${pattern}${suffix}`;
	}

	// Show overlay
	$overlay.removeAttribute(g.hidden_attr);
}


/**
 * Close the compare overlay
 */
function close_compare_overlay() {
	if ($overlay) {
		$overlay.setAttribute(g.hidden_attr, '');
	}
}


/**
 * Strip reveal-related attributes from an element and its descendants
 * @param {HTMLElement} $el
 */
function strip_reveal_attrs($el) {
	const attrs = ['reveal', 'revealing', 'subreveal', 'delay', 'aligned', 'reveal-group'];
	const selector = attrs.map((a) => `[${a}]`).join(', ');

	// Strip from root element
	attrs.forEach((a) => {
		$el.removeAttribute(a);
		$el.setAttribute('revealed', '');
	});

	// Strip from all descendants
	$el.querySelectorAll(selector).forEach(($child) => {
		attrs.forEach((a) => $child.removeAttribute(a));
		$child.setAttribute('revealed', '');
	});
}
